import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Box, Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    useTheme,
    Alert,
    TextField,
    Checkbox,
    FormControlLabel,
    Divider,
    Paper,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    ContentCopy as CopyIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Wallet as WalletIcon,
    Add as AddIcon
} from "@mui/icons-material";

import { ReactComponent as PhantomWalletLogo } from '../../assets/svg/Phantom-Logo-Black.svg';
import xVerseLogo from '../../assets/xverseLogo.webp';
import { AuthContext } from "../../state/context/auth.context";
import { WalletsContext } from "../../state/context/wallets.context";
import { generateWallet, saveWalletToLocalStorage, loginWithGenerated } from "../../utils/WalletProviders/generated";
import { toast } from "react-toastify";

const WalletProvidersNew = ({ open, onClose, type = "LOGIN" }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { state, login } = useContext(AuthContext);
    const { setMainWallet } = useContext(WalletsContext);
    const { isConnected, address } = state.user || {};
    const { mainWallet } = state;

    const [generatedWallet, setGeneratedWallet] = useState(null);
    const [walletName, setWalletName] = useState('My Generated Wallet');
    const [showPrivateKey, setShowPrivateKey] = useState(false);
    const [hasBackedUp, setHasBackedUp] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [step, setStep] = useState('selection'); // 'selection', 'generated', 'saved'

    const handleClose = () => {
        setStep('selection');
        setGeneratedWallet(null);
        setHasBackedUp(false);
        setShowPrivateKey(false);
        setWalletName('My Generated Wallet');
        onClose();
    };

    const handleGenerateWallet = async () => {
        setIsGenerating(true);
        try {
            const wallet = await generateWallet(); // Now async
            setGeneratedWallet(wallet);
            setStep('generated');
        } catch (error) {
            toast.error(`Failed to generate wallet: ${ error.message }`);
        }
        setIsGenerating(false);
    };

    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${ label } copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy to clipboard');
        });
    };

    const handleSaveWallet = async () => {
        if (!generatedWallet || !hasBackedUp) return;

        try {
            // Save wallet to localStorage
            const savedWallet = saveWalletToLocalStorage(generatedWallet, walletName);

            if (type === "LOGIN") {
                // Login with the generated wallet
                const loginData = await loginWithGenerated(generatedWallet);
                // Update auth context (this might need to be adapted based on your login action)
                login(loginData);
            }

            if (type === "CONNECT_MAIN_WALLET") {
                setMainWallet(savedWallet);
            }

            toast.success('Wallet saved successfully!');
            setStep('saved');

            // Close dialog after a short delay
            setTimeout(() => {
                handleClose();
            }, 2000);
        } catch (error) {
            toast.error(`Failed to save wallet: ${ error.message }`);
        }
    };

    const renderSelectionStep = () => (
        <>
            <DialogTitle>{t('common.connectWallet')}</DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    {t('common.chooseWalletProvider')}
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                    {/* Existing wallet providers */}
                    <Button
                        sx={{
                            background: "#AB9FF2",
                            height: 48,
                            justifyContent: "center",
                            px: 3,
                        }}
                        onClick={() => {
                            if (type === "LOGIN") {
                                login("PHANTOM");
                            }
                            if (type === "CONNECT_MAIN_WALLET") {
                                setMainWallet("PHANTOM");
                            }
                        }}
                    >
                        <PhantomWalletLogo style={{ height: 30, marginRight: 12 }} />
                    </Button>

                    <Button
                        sx={{
                            border: `1px solid ${ theme.palette.divider }`,
                            height: 48,
                            justifyContent: "center",
                            px: 3,
                        }}
                        onClick={() => {
                            if (type === "LOGIN") {
                                login("XVERSE");
                            }
                            if (type === "CONNECT_MAIN_WALLET") {
                                setMainWallet("XVERSE");
                            }
                        }}
                    >
                        <Box
                            component="img"
                            src={xVerseLogo}
                            alt="Xverse"
                            sx={{ height: 30, marginRight: 12 }}
                        />
                    </Button>

                    <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Or create a new wallet
                        </Typography>
                    </Divider>

                    {/* New generated wallet option */}
                    <Button
                        variant="outlined"
                        sx={{
                            height: 48,
                            justifyContent: "center",
                            px: 3,
                            borderColor: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main + '10',
                            }
                        }}
                        onClick={handleGenerateWallet}
                        disabled={isGenerating}
                    >
                        <AddIcon sx={{ marginRight: 1 }} />
                        <Typography>
                            {isGenerating ? 'Generating...' : 'Generate New Wallet'}
                        </Typography>
                    </Button>
                </Box>
            </DialogContent>
        </>
    );

    const renderGeneratedStep = () => (
        <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <WalletIcon sx={{ mr: 1 }} />
                Wallet Generated Successfully
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>Important:</strong> Please save your private key in a secure location.
                        You will need it to recover your wallet. Never share it with anyone!
                    </Typography>
                </Alert>

                <TextField
                    fullWidth
                    label="Wallet Name"
                    value={walletName}
                    onChange={(e) => setWalletName(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Paper elevation={1} sx={{ p: 2, mb: 2, backgroundColor: theme.palette.grey[50] }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Address:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', flexGrow: 1, color: '#000000', fontWeight: 600, fontSize: '0.9rem' }}>
                            {generatedWallet?.address}
                        </Typography>
                        <Tooltip title="Copy address">
                            <IconButton
                                size="small"
                                onClick={() => handleCopyToClipboard(generatedWallet?.address, 'Address')}
                            >
                                <CopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Paper>

                <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[50], border: `1px solid ${ theme.palette.divider }` }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">
                            Private Key:
                        </Typography>
                        <Box>
                            <Tooltip title="Copy private key">
                                <IconButton
                                    size="small"
                                    onClick={() => handleCopyToClipboard(generatedWallet?.privateKey, 'Private Key')}
                                >
                                    <CopyIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showPrivateKey ? "Hide" : "Show"}>
                                <IconButton
                                    size="small"
                                    onClick={() => setShowPrivateKey(!showPrivateKey)}
                                >
                                    {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', color: '#000000', fontWeight: 600, fontSize: '0.9rem' }}>
                        {showPrivateKey ? generatedWallet?.privateKey : '•'.repeat(52)}
                    </Typography>
                </Paper>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hasBackedUp}
                            onChange={(e) => setHasBackedUp(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            I have safely backed up my private key and understand that I will lose access to my wallet if I lose it.
                        </Typography>
                    }
                />
            </DialogContent>
        </>
    );

    const renderSavedStep = () => (
        <>
            <DialogTitle sx={{ textAlign: 'center' }}>
                ✅ Wallet Saved Successfully!
            </DialogTitle>
            <DialogContent sx={{ textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    Your wallet has been saved and is ready to use.
                </Typography>
            </DialogContent>
        </>
    );

    return (
        <Dialog
            open={type === "LOGIN" ? (open && !isConnected) : (open && !mainWallet)}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            sx={{
                "& .MuiDialog-paper": {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "16px",
                },
            }}
        >
            {step === 'selection' && renderSelectionStep()}
            {step === 'generated' && renderGeneratedStep()}
            {step === 'saved' && renderSavedStep()}

            <DialogActions>
                {step === 'generated' && (
                    <Button
                        variant="contained"
                        onClick={handleSaveWallet}
                        disabled={!hasBackedUp || !walletName.trim()}
                    >
                        Save Wallet & Continue
                    </Button>
                )}
                <Button onClick={handleClose} color="secondary">
                    {step === 'saved' ? 'Done' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WalletProvidersNew;
